/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, bool, string, func, shape,
} from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router';
import { useQuery } from '@apollo/react-hooks';
import  { compose } from 'recompose';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';

import CoreBrowsingBuilder from './CoreBrowsingBuilder';
import GraphqlSearchContainer from '../GraphqlSearchPage/GraphqlSearchContainer';
import CoreBrowsingRedirectContainer from './CoreBrowsingRedirectContainer';

import { trackModule } from '../../../helpers/tracking/common/commonTrackingHelpers';
import findURL from '../../../gql/queries/findURL';
import { GRAPHQL_ENV } from '../../../gql';
import { getIsBot } from '../../../../state/ducks/App/App-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getFeatureFlags } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { setLanguageName, setCountryName } from '../../../../state/ducks/Member/ducks/Auth/helper/helper';
import { setPageInformation } from '../../../../state/ducks/App/App-Actions';
import { determineCollectionPrefixPath, determineProductPrefixPath } from '../../../helpers/determineInternationalizationPath';

/*
    Component Intent
        - enable Collection page url
*/
const CollectionBrowsingContainer = ({
    location: { pathname, search }, match: { params: { slug, lang, countryCode } }, brand, featureFlags, isBot = false, is404, productUnavailableName, pageInfo,
}) => {
    const isValidLanguagePath = () => {
        if (lang?.includes('en')
            || lang?.includes('es')
            || lang?.includes('fr')) {
            return true;
        }
        return false;
    };

    let multiLanguagePathName = '/';
    if (!isValidLanguagePath()) {
        // redirect to /en as fallback
        const urlPathStack = String(pathname).split('/');
        const formattedUrl = urlPathStack.slice(2).join('/');
        return <Redirect to={`/en/${formattedUrl}`} />;
    }
    if (isValidLanguagePath && countryCode && lang) {
        multiLanguagePathName = `/${slug}`;
        setLanguageName(`/${lang}`);
        setCountryName(`/${countryCode}`);
    } else if (isValidLanguagePath && !countryCode && lang) {
        multiLanguagePathName = `/${slug}`;
        setLanguageName(`/${lang}`);
        setCountryName('/');
    }
    if (pathname.includes('searchterm')) {
        const pathnameArr = pathname.split('/');
        const searchTerm = pathnameArr[2];
        return <GraphqlSearchContainer brand={brand} searchTerm={searchTerm} />;
    }
    // on page transition, bring user back to top of page - currently not working on back nav when cached
    useEffect(() => {
        if (typeof window !== 'undefined' && window.scroll) {
            window.scroll(0, 0);
        }
    });
    if (multiLanguagePathName && multiLanguagePathName === '') {
        multiLanguagePathName = '/';
    }

    const FIND_URL_QUERY = findURL(brand.domain, multiLanguagePathName);
    const { loading, data, error } = useQuery(FIND_URL_QUERY);

    if (loading) {
        return null;
    }

    if (error || !data) {
        mbpLogger.logError({
            query: FIND_URL_QUERY,
            component: 'CollectionBrowsingContainer.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }

    // correct collection and product prefix if wrong prifix with /c/product and /p/collection
    if (data?.findURL?.type === 'category' && pathname.indexOf('/p/') > -1) {
        const slugPath = `/${slug}`;
        return <Redirect to={determineCollectionPrefixPath(slugPath, false)} />;
    }
    if (data?.findURL?.type === 'product' && pathname.indexOf('/c/') > -1) {
        const slugPath = `/${slug}`;
        return <Redirect to={determineProductPrefixPath(slugPath, false)} />;
    }

    let pageData =  data.findURL;
    pageData = { path: multiLanguagePathName, search, ...pageData };

    if (mbpUtil.getEnv('REACT_APP_MBP_LOGGER_CONSOLE') === 'DEBUG') {
        console.log('CollectionBrowsingContainer pageData', pageData);
    }
    pageInfo(pageData?.type);
    if (featureFlags['is-redirect-testing-enabled']
        && !isBot
        && pageData?.redirectTesting
    ) {
        mbpLogger.logDebug({ message: 'CollectionBrowsingContainer ... redirecting' });

        return (
            <CoreBrowsingRedirectContainer
                page={pageData}
                featureFlags={featureFlags}
                brand={brand}
            />
        );
    }

    mbpLogger.logDebug({ message: `CollectionBrowsingContainer ... sending to CoreBrowsingBuilder.  is404? ${is404}` });
    return (
        <CoreBrowsingBuilder
            brand={brand}
            page={pageData}
            is404={is404}
            productUnavailableName={productUnavailableName}
        />
    );
};

CollectionBrowsingContainer.propTypes = {
    location: object.isRequired,
    brand: shape({
        domain: string.isRequired,
    }),
    featureFlags: shape({
        'is-redirect-testing-enabled': bool,
    }).isRequired,
    isBot: bool.isRequired,
    is404: bool,
    productUnavailableName: string,
    pageInfo: func.isRequired,
    match: {
        params: {
            lang: string.isRequired,
            countryCode: string.isRequired,
            slug: string.isRequired,
        },
    },
};

CollectionBrowsingContainer.defaultProps = {
    is404: false,
    productUnavailableName: '',
    brand: {
        domain: '',
    },
    match: {
        params: {
            lang: '',
            countryCode: '',
            slug: '',
        },
    },
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    featureFlags: getFeatureFlags(state),
    isBot: getIsBot(state),
});

const mapDispatchToProps = (dispatch) => ({
    pageInfo: bindActionCreators(setPageInformation, dispatch),
});

const enhance = compose(
    trackModule('CollectionBrowsingContainer'),
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(CollectionBrowsingContainer);
